import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { courseAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiTrash2, FiEye, FiUsers, FiFolder, FiBook, FiToggleLeft, FiToggleRight } from 'react-icons/fi';

const Courses = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const response = await courseAPI.getCourses();
      if (response.data.success) {
        setCourses(response.data.data);
      }
    } catch (error) {
      toast.error('Error fetching courses');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (course) => {
    const action = course.status === 1 ? 'unpublish' : 'publish';
    if (!window.confirm(`Are you sure you want to ${action} "${course.title}"?`)) {
      return;
    }

    try {
      const response = await courseAPI.toggleCourseStatus(course.id);
      if (response.data.success) {
        toast.success(response.data.message);
        fetchCourses();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const handlePositionChange = async (id, position) => {
    try {
      const positionValue = parseInt(position, 10);
      
      if (isNaN(positionValue) || positionValue < 0) {
        toast.error('Position must be a valid number >= 0');
        return;
      }
      
      const response = await courseAPI.updateCoursePosition(id, positionValue);
      if (response.data.success) {
        toast.success('Position updated');
        fetchCourses();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update position');
    }
  };

  const handleDelete = async (courseId, courseTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${courseTitle}"? This will remove all folders, content, and associated data.`)) {
      return;
    }

    try {
      const response = await courseAPI.deleteCourse(courseId);
      if (response.data.success) {
        toast.success('Course deleted successfully');
        fetchCourses();
      }
    } catch (error) {
      toast.error('Error deleting course');
      console.error(error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-full mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4 md:mb-6 gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Course List</h1>
          <p className="text-sm md:text-base text-gray-600 mt-1">Manage all courses for your institute</p>
        </div>
        <button 
          onClick={() => navigate('/courses/add')}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm md:text-base min-h-[44px] w-full sm:w-auto"
        >
          <FiPlus /> Create Course
        </button>
      </div>

      {courses.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 md:py-16 bg-gray-50 rounded-lg">
          <FiBook size={48} className="md:w-16 md:h-16 text-gray-300 mb-4" />
          <h3 className="text-lg md:text-xl font-semibold text-gray-700 mb-2">No courses yet</h3>
          <p className="text-sm md:text-base text-gray-500 mb-6">Create your first course to get started</p>
          <button 
            onClick={() => navigate('/courses/add')}
            className="flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium min-h-[44px]"
          >
            <FiPlus /> Create Course
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">ID</th>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Title</th>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Category</th>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Sub Category</th>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Validity</th>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Tag</th>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Type</th>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Position</th>
                  <th className="px-3 md:px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody>
                {courses.map((course) => (
                  <tr key={course.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="px-3 md:px-4 py-3 text-xs md:text-sm text-gray-900">{course.id}</td>
                    <td className="px-3 md:px-4 py-3">
                      <div className="flex items-center gap-2 md:gap-3">
                        {course.course_thumbnail && (
                          <img 
                            src={`${course.course_thumbnail}`} 
                            alt={course.title}
                            className="w-10 h-10 md:w-12 md:h-12 object-cover rounded"
                          />
                        )}
                        <div>
                          <p className="text-xs md:text-sm font-medium text-gray-900">{course.title}</p>
                          <div className="flex items-center gap-2 md:gap-3 mt-1 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <FiFolder size={12} /> {course.folders_count}
                            </span>
                            <span className="flex items-center gap-1">
                              <FiUsers size={12} /> {course.sales_count} Sales
                            </span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 md:px-4 py-3 text-xs md:text-sm text-gray-600">{course.tradegroup_name || '-'}</td>
                    <td className="px-3 md:px-4 py-3 text-xs md:text-sm text-gray-600">{course.trade_names || '-'}</td>
                    <td className="px-3 md:px-4 py-3 text-xs md:text-sm text-gray-600">
                      {course.validity > 0 ? `${course.validity} months` : course.expiry || '-'}
                    </td>
                    <td className="px-3 md:px-4 py-3 text-xs md:text-sm text-gray-600">{course.tag || '-'}</td>
                    <td className="px-3 md:px-4 py-3">
                      <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${
                        course.free_course == 1
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {course.free_course == 1 ? 'Free' : 'Paid'}
                      </span>
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <button
                        onClick={() => handleToggleStatus(course)}
                        className="inline-flex items-center gap-1"
                      >
                        <span className={`inline-block px-2 md:px-3 py-1 text-xs font-medium rounded-full ${
                          course.status === 1
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {course.status === 1 ? 'Published' : 'Unpublished'}
                        </span>
                      </button>
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <input
                        type="number"
                        value={course.position || 0}
                        onChange={(e) => handlePositionChange(course.id, e.target.value)}
                        className="w-16 md:w-20 px-2 py-1 border border-gray-300 rounded text-xs md:text-sm focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none"
                        min="0"
                      />
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <div className="flex justify-end items-center gap-1 md:gap-2">
                        <button
                          onClick={() => navigate(`/courses/${course.id}/viewcontent`)}
                          className="p-2 text-gray-600 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors min-w-[36px] min-h-[36px] flex items-center justify-center"
                          title="View Contents"
                        >
                          <FiEye size={16} />
                        </button>
                        <button
                          onClick={() => navigate(`/courses/${course.id}/edit`, { state: { course } })}
                          className="p-2 text-gray-600 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors min-w-[36px] min-h-[36px] flex items-center justify-center"
                          title="Edit Course"
                        >
                          <FiEdit2 size={16} />
                        </button>
                        <button
                          onClick={() => navigate(`/courses/${course.id}/addcontent`)}
                          className="p-2 text-gray-600 hover:bg-green-50 hover:text-green-600 rounded-lg transition-colors min-w-[36px] min-h-[36px] flex items-center justify-center"
                          title="Add Content"
                        >
                          <FiFolder size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(course.id, course.title)}
                          className="p-2 text-gray-600 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors min-w-[36px] min-h-[36px] flex items-center justify-center"
                          title="Delete Course"
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Courses;
